import React from 'react';

import backend from 'utils/backend';

// The playback links of one invitation, from Loom's staff recordings route. A link expires after
// 10 minutes, so a player calls `refresh` to get new links when it needs them. Players that need new
// links at the same time share one request.
const useInvitationRecordings = (invitationId, enabled) => {
  const [recordings, setRecordings] = React.useState(null);
  const [loading, setLoading] = React.useState(enabled);
  const [error, setError] = React.useState(null);

  const request = React.useRef(null);
  const isMounted = React.useRef(true);

  React.useEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;
    };
  }, []);

  const refresh = React.useCallback(() => {
    if (!request.current) {
      request.current = backend.loom.invitation.getRecordings(invitationId)
          .then((result) => {
            if (isMounted.current) {
              setRecordings(result);
              setError(null);
            }
            return result;
          })
          .finally(() => {
            request.current = null;
          });
    }

    return request.current;
  }, [invitationId]);

  React.useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }

    let isCancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);

      try {
        await refresh();
      } catch (loadError) {
        console.error(loadError);
        if (!isCancelled) setError(loadError);
      } finally {
        if (!isCancelled) setLoading(false);
      }
    };

    load();

    return () => {
      isCancelled = true;
    };
  }, [enabled, refresh]);

  return {recordings, loading, error, refresh};
};

export default useInvitationRecordings;
